<template>
  <header class="app-header" :class="{ 'app-header--minimal': minimal, 'app-header--room': room }">
    <div class="header-content">
      <router-link to="/" class="logo">
        <span class="logo-icon">🎤</span>
        <span class="logo-text">mykaraoke.world</span>
      </router-link>

      <div v-if="room" id="room-header-actions" class="room-header-actions"></div>

      <div v-else-if="!minimal" class="auth-area">
        <div v-if="isLoading" class="auth-loading">
          <span class="loading-spinner"></span>
        </div>

        <button v-else-if="!isLoggedIn" @click="showSignInModal = true" class="sign-in-button">
          Sign in
        </button>

        <UserDropdown
          v-else
          :user="user"
          @sign-out="handleSignOut"
        />
      </div>
    </div>

    <SignInModal
      v-if="!minimal && !room && showSignInModal"
      ref="signInModalRef"
      @close="showSignInModal = false"
      @google-sign-in="handleGoogleSignIn"
      @apple-sign-in="handleAppleSignIn"
      @email-sign-in="handleEmailSignIn"
      @create-account="handleCreateAccount"
      @password-reset="handlePasswordReset"
    />
  </header>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useAuth } from '../composables/useAuth'
import UserDropdown from './UserDropdown.vue'
import SignInModal from './SignInModal.vue'

export default {
  name: 'AppHeader',
  components: {
    UserDropdown,
    SignInModal
  },
  props: {
    minimal: {
      type: Boolean,
      default: false
    },
    room: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const {
      user,
      isLoggedIn,
      isLoading,
      signInWithGoogle,
      signInWithApple,
      signInWithEmail,
      createAccountWithEmail,
      sendPasswordReset,
      signOut
    } = useAuth()

    const showSignInModal = ref(false)
    const signInModalRef = ref(null)

    watch(
      () => props.minimal || props.room,
      (isRestrictedHeader) => {
        if (isRestrictedHeader) {
          showSignInModal.value = false
        }
      },
      { immediate: true }
    )

    const handleGoogleSignIn = async () => {
      const result = await signInWithGoogle()
      if (result.success) {
        showSignInModal.value = false
      } else if (result.error) {
        signInModalRef.value?.setError(result.error)
      } else {
        signInModalRef.value?.setLoading(false)
      }
    }

    const handleAppleSignIn = async () => {
      const result = await signInWithApple()
      if (result.success) {
        showSignInModal.value = false
      } else if (result.error) {
        signInModalRef.value?.setError(result.error)
      } else {
        signInModalRef.value?.setLoading(false)
      }
    }

    const handleEmailSignIn = async ({ email, password }) => {
      const result = await signInWithEmail(email, password)
      if (result.success) {
        showSignInModal.value = false
      } else {
        signInModalRef.value?.setError(result.error)
      }
    }

    const handleCreateAccount = async ({ email, password, displayName }) => {
      const result = await createAccountWithEmail(email, password, displayName)
      if (result.success) {
        showSignInModal.value = false
      } else {
        signInModalRef.value?.setError(result.error)
      }
    }

    const handlePasswordReset = async (email) => {
      const result = await sendPasswordReset(email)
      if (result.success) {
        signInModalRef.value?.setSuccess('Password reset email sent! Check your inbox.')
      } else {
        signInModalRef.value?.setError(result.error)
      }
    }

    const handleSignOut = async () => {
      await signOut()
    }

    const openSignInModal = () => {
      if (props.minimal || props.room) return
      showSignInModal.value = true
    }

    const onOpenSignIn = () => {
      openSignInModal()
    }

    onMounted(() => {
      window.addEventListener('auth:open-sign-in', onOpenSignIn)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('auth:open-sign-in', onOpenSignIn)
    })

    return {
      user,
      isLoggedIn,
      isLoading,
      showSignInModal,
      signInModalRef,
      handleGoogleSignIn,
      handleAppleSignIn,
      handleEmailSignIn,
      handleCreateAccount,
      handlePasswordReset,
      handleSignOut
    }
  }
}
</script>

<style scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1000;
}

.app-header--minimal {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-bottom-color: transparent;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-header--minimal .header-content {
  max-width: none;
  padding: 0 clamp(1.25rem, 4vw, 2rem);
}

.app-header--room {
  background: rgba(6, 5, 4, 0.96);
  border-bottom-color: rgba(255, 165, 0, 0.12);
}

.app-header--room .header-content {
  max-width: none;
  padding: 0 clamp(0.75rem, 2vw, 1.5rem);
}

.room-header-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: #ffa500;
}

.logo-icon {
  font-size: 1.5rem;
}

.logo-text {
  font-size: 1.1rem;
  font-weight: 500;
}

.auth-area {
  display: flex;
  align-items: center;
}

.auth-loading {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 165, 0, 0.3);
  border-radius: 50%;
  border-top-color: #ffa500;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.sign-in-button {
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid rgba(255, 165, 0, 0.5);
  border-radius: 6px;
  color: #ffa500;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.sign-in-button:hover {
  background: rgba(255, 165, 0, 0.1);
  border-color: #ffa500;
}

@media (max-width: 420px) {
  .app-header--room .header-content {
    padding-inline: 0.65rem;
  }

  .app-header--room .logo {
    gap: 0;
  }

  .app-header--room .logo-icon {
    display: none;
  }

  .app-header--room .logo-text {
    font-size: 0.95rem;
  }
}
</style>
